import { useState, useEffect, useMemo, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
	Paper,
	Button,
	TextField,
	ListItemText,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Autocomplete,
	Typography,
} from "@mui/material";
import { DataGrid, GridActionsCell, GridActionsCellItem } from "@mui/x-data-grid";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
//cmp
import Na from "../na";
import Svg from "../svg";
// contexts
import { useUserData } from "../../context/UserDataContext";
// hooks
import useSend from "../../hooks/useSend";
// services
import Gupport from "../../services/gupport";
import { sortAlphabetically } from "../../services/l10n";
import { icons } from "@local/theme";
// types
import type { GridRowId, GridColDef, GridRowIdGetter } from "@mui/x-data-grid";
import type { GatewayUser } from "../../types/user";
import type { GatewayId } from "../../types/gateway";
import type { CmdActivateGateway, CmdDeactivateGateway, CmdDisinviteUser, CmdGetUsers, CmdInviteUser, CmdSearch, SearchDataUser } from "../../types/gupport";

type Props = Readonly<{
	gatewayId: GatewayId;
	gatewayLabel: string;
}>;

interface SelectedUser {
	id: string;
	label: string;
	primary: string;
	secondary: string;
}
const handleGetRowId: GridRowIdGetter<GatewayUser> = (row) => (row.username);

const GatewayUsers = ({ gatewayId, gatewayLabel }: Props) => {
	const send = useSend();

	const theme = useTheme();
	const { t } = useTranslation();

	const { ready } = useUserData();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [owner, setOwner] = useState<GatewayUser | undefined>(undefined);
	const [usersWithoutOwner, setUsersWithoutOwner] = useState<ReadonlyArray<GatewayUser>>([]);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [buttonDisable, setButtonDisable] = useState(true);
	const [showErrorDialog, setShowErrorDialog] = useState(false);
	const [showInviteDialog, setShowInviteDialog] = useState(false);
	const [showActivateDialog, setShowActivateDialog] = useState(false);
	const [showDisinviteDialog, setShowDisinviteDialog] = useState(false);
	const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [options, setOptions] = useState<Array<SelectedUser>>([]);
	const [selectedUser, setSelectedUser] = useState<SelectedUser | GatewayUser | undefined>(undefined);
	const [isInviting, setIsInviting] = useState(false);
	const [isActivating, setIsActivating] = useState(false);
	const [isDeactivating, setIsDeactivating] = useState(false);
	const [isDisinviting, setIsDisinviting] = useState(false);

	const isDisinviteUserAvail = Boolean(Gupport.disinviteUser);

	const getUsers = (_gatewayId: GatewayId) => {
		const cmd = {
			action: "getUsers",
			gatewayId: _gatewayId,
		} as const satisfies CmdGetUsers;
		send(cmd, (_error, msg) => {
			if (!_error && msg?.payload.status === "ok") {
				const users = msg.payload.data;
				setError(null);
				setOwner(users.find((user) => (user.level === "owner")));
				setUsersWithoutOwner(users.filter((user) => (user.level !== "owner")));
			} else {
				setError(_error?.message ?? msg.payload.data);
				setOwner(undefined);
				setUsersWithoutOwner([]);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		if (ready) {
			getUsers(gatewayId);
		}
	}, [gatewayId, ready]);

	const openInviteDialog = () => {
		setShowInviteDialog(true);
		setButtonDisable(true);
	};

	const openActivateDialog = () => {
		setShowActivateDialog(true);
		setButtonDisable(true);
	};

	const handleClose = () => {
		setShowErrorDialog(false);
		setShowInviteDialog(false);
		setShowActivateDialog(false);
		setShowDisinviteDialog(false);
		setShowDeactivateDialog(false);
	};

	const handleUpdateInput = (event, value) => {
		if (value.length < 3) { // search need at least 3 chars
			setButtonDisable(true);
			setOptions([]);
			setOptionsLoading(false);
			return;
		}

		setOptionsLoading(true);

		const cmd = {
			action: "search",
			kind: "user",
			query: value,
		} as const satisfies CmdSearch;
		send(cmd, (_error, msg) => {
			if (!_error && msg?.payload.status === "ok") {
				// `subId` is only an equal search possible. `username` and `email` is startsWidth search.
				const optionsData = (msg.payload.data as ReadonlyArray<SearchDataUser>)
					.filter((item, index, array) => (
						array.findIndex((el) => (el.id === item.id)) === index // TODO: remove deduplication in future
					))
					.map((user) => ({
						id: user.id,
						label: `${user.name ?? user.username} (${user.username}) [${user.email}]`,
						primary: user.name ?? user.username,
						secondary: `${user.username} (${user.email})`,
					}));

				const sortedOptionsData = optionsData.toSorted((optionA, optionB) => {
					const aText = (`${optionA.label}#${optionA.id}`).toLowerCase();
					const bText = (`${optionB.label}#${optionB.id}`).toLowerCase();
					return sortAlphabetically(aText, bText);
				});

				setButtonDisable(false);
				setOptions(sortedOptionsData);
			} else {
				setButtonDisable(true);
				setOptions([]);
			}
			setOptionsLoading(false);
		});
	};

	const handleNewRequest = (event, value/*, reason, details*/) => {
		if (value) {
			setButtonDisable(false);
			setSelectedUser(value);
		} else {
			setButtonDisable(true);
		}
	};

	const disinviteUser = () => {
		setIsDisinviting(true);
		const cmd = {
			action: "disinviteUser",
			gatewayId: gatewayId,
			username: selectedUser?.username,
		} as const satisfies CmdDisinviteUser;
		send(cmd, (_error, msg) => {
			if (!_error && msg?.payload.status === "ok") {
				getUsers(gatewayId);
			} else {
				setShowErrorDialog(true);
				setErrorMessage(t("gateway.disinvited"));
			}
			setSelectedUser(undefined);
			setShowDisinviteDialog(false);
			setIsDisinviting(false);
		});
	};

	const inviteUser = () => {
		setIsInviting(true);
		const cmd = {
			action: "inviteUser",
			gatewayId: gatewayId,
			username: selectedUser?.id,
		} as const satisfies CmdInviteUser;
		send(cmd, (_error, msg) => {
			if (!_error && msg?.payload.status === "ok") {
				getUsers(gatewayId);
			} else {
				setShowErrorDialog(true);
				setErrorMessage(t("gateway.inviteError"));
			}
			setShowInviteDialog(false);
			setIsInviting(false);
		});
	};

	const activateGateway = () => {
		setIsActivating(true);
		const cmd = {
			action: "activateGateway",
			gatewayId: gatewayId,
			username: selectedUser?.id,
		} as const satisfies CmdActivateGateway;
		send(cmd, (_error, msg) => {
			if (!_error && msg?.payload.status === "ok") {
				getUsers(gatewayId);
			} else {
				setShowErrorDialog(true);
				setErrorMessage(t("gateway.activateError"));
			}
			setShowActivateDialog(false);
			setIsActivating(false);
		});
	};

	const deactivateGateway = () => {
		setIsDeactivating(true);
		const cmd = {
			action: "deactivateGateway",
			gatewayId: gatewayId,
			username: selectedUser?.username,
		} as const satisfies CmdDeactivateGateway;
		send(cmd, (_error, msg) => {
			if (!_error && msg?.payload.status === "ok") {
				getUsers(gatewayId);
			} else {
				setShowErrorDialog(true);
				setErrorMessage(t("gateway.deactivated"));
			}
			setSelectedUser(undefined);
			setShowDeactivateDialog(false);
			setIsDeactivating(false);
		});
	};

	const openDisinviteDialog = useCallback((id: GridRowId) => (
		() => {
			setSelectedUser(usersWithoutOwner.find((user) => (user.username === id)));
			setShowDisinviteDialog(true);
		}
	), [usersWithoutOwner]);

	const openDeactivateDialog = useCallback(() => (
		() => {
			setSelectedUser(owner);
			setShowDeactivateDialog(true);
		}
	), [owner]);

	const columns = useMemo(() => ([
		{
			field: "email",
			headerName: t("gateway.username"),
			flex: 3,
			renderCell: (params) => (
				<a href={`user_desc.html#/users/${encodeURIComponent(params.row.username)}/general`} target="_blank" rel="noopener noreferrer" style={{ color: theme.vars.palette.primary.main, textDecoration: "none" }}>
					{params.row.email ?? params.row.emails?.join(", ") ?? params.row.username}
				</a>
			),
			valueGetter: (_value, row) => (row.email ?? row.emails?.join(", ") ?? row.username),
		},
		{
			field: "username",
			headerName: t("gateway.userId"),
			flex: 3,
		},
		{
			field: "name",
			headerName: t("gateway.uName"),
			flex: 1,
			renderCell: (params) => (params.row.name ?? <Na />),
		},
		{
			field: "level",
			headerName: t("gateway.level"),
			flex: 1,
			renderCell: (params) => (params.row.level ?? <Na />),
		},
		{
			field: "actions",
			headerName: "Action",
			type: "actions",
			flex: 1,
			renderCell: (params) => (
				<GridActionsCell {...params}>
					{(params.row.level === "owner") ?
						<GridActionsCellItem
							key={params.id}
							label="Deactivate"
							icon={<icons.Block color="danger" />}
							onClick={openDeactivateDialog()}
						/>
						: (params.row.level === "user" && isDisinviteUserAvail) ?
							<GridActionsCellItem
								key={params.id}
								label="Disinvite"
								icon={<Svg src="actions/removeCircleOutlined.svg" color={theme.vars.palette.danger.main} />}
								onClick={openDisinviteDialog(params.id)}
							/>
							: null
					}
				</GridActionsCell>
			),
		},
	] as const satisfies ReadonlyArray<GridColDef<GatewayUser>>), [t, theme.vars.palette.primary.main, theme.vars.palette.danger.main, isDisinviteUserAvail, openDeactivateDialog, openDisinviteDialog]);

	if (error) {
		return <div>{error}</div>;
	}

	return (
		<>
			<section style={{ marginBottom: "16px" }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
					<Typography variant="subtitle1">{t("gateway.owner")}</Typography>
					{Boolean(Gupport.activateGateway) &&
						<Button disabled={loading || owner !== undefined} variant="contained" onClick={openActivateDialog}>{t("gateway.activateGt")}</Button>
					}
				</div>
				<Paper>
					<DataGrid
						loading={loading}
						columns={columns}
						rows={(owner === undefined) ? [] : [owner]}
						getRowId={handleGetRowId}
					/>
				</Paper>
			</section>
			<section style={{ marginBottom: "16px" }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
					<Typography variant="subtitle1">{t("gateway.user", { count: usersWithoutOwner.length })}</Typography>
					{Boolean(Gupport.inviteUser) &&
						<Button disabled={loading} variant="contained" onClick={openInviteDialog}>{t("gateway.inviteUser")}</Button>
					}
				</div>
				<Paper>
					<DataGrid
						loading={loading}
						columns={columns}
						rows={usersWithoutOwner}
						getRowId={handleGetRowId}
					/>
				</Paper>
			</section>
			<Dialog
				fullWidth={true}
				open={showErrorDialog}
				onClose={handleClose}
			>
				<DialogTitle>{t("dialog.error")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{errorMessage}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>{t("dialog.ok")}</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth={true}
				maxWidth="md"
				open={showInviteDialog}
				onClose={handleClose}
			>
				<DialogTitle>{t("gateway.inviteUser")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{t("gateway.inviteUserMsg")} <strong>{gatewayLabel}</strong>
					</DialogContentText>
					<Autocomplete
						id="autocomplete_invite_user"
						fullWidth={true}
						loading={optionsLoading}
						options={options}
						filterOptions={createFilterOptions({ ignoreCase: true })}
						isOptionEqualToValue={(option, value) => (option.id === value.id)}
						onInputChange={handleUpdateInput}
						onChange={handleNewRequest}
						renderInput={(params) => (
							<TextField autoFocus={true} {...params} placeholder={t("gateway.userHint")} />
						)}
						renderOption={({ key, ...optionProps }, option/*, state, ownerState*/) => (
							<li key={key} {...optionProps}><ListItemText primary={option.primary} secondary={option.secondary} /></li>
						)}
					/>
				</DialogContent>
				<DialogActions>
					<Button disabled={selectedUser === undefined || buttonDisable} loading={isInviting} onClick={inviteUser}>{t("gateway.invite")}</Button>
					<Button color="inherit" onClick={handleClose}>{t("dialog.cancel")}</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth={true}
				open={showActivateDialog}
				onClose={handleClose}
			>
				<DialogTitle>{t("gateway.activateGt")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{t("gateway.activateMsg")}
					</DialogContentText>
					<Autocomplete
						id="autocomplete_activate_gateway"
						fullWidth={true}
						loading={optionsLoading}
						options={options}
						filterOptions={createFilterOptions({ ignoreCase: true })}
						isOptionEqualToValue={(option, value) => (option.id === value.id)}
						onInputChange={handleUpdateInput}
						onChange={handleNewRequest}
						renderInput={(params) => (
							<TextField autoFocus={true} {...params} placeholder={t("gateway.userHint")} />
						)}
						renderOption={({ key, ...optionProps }, option/*, state, ownerState*/) => (
							<li key={key} {...optionProps}><ListItemText primary={option.primary} secondary={option.secondary} /></li>
						)}
					/>
				</DialogContent>
				<DialogActions>
					<Button disabled={selectedUser === undefined || buttonDisable} loading={isActivating} onClick={activateGateway}>{t("gateway.activateBtn")}</Button>
					<Button color="inherit" onClick={handleClose}>{t("dialog.cancel")}</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth={true}
				open={showDeactivateDialog}
				onClose={handleClose}
			>
				<DialogTitle>{t("gateway.deactivateDialogTitle")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<Trans
							i18nKey="gateway.deactivateDialogMsg"
							values={{ label: gatewayLabel }}
						/>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button color="danger" loading={isDeactivating} onClick={deactivateGateway}>{t("gateway.deactivate")}</Button>
					<Button color="inherit" onClick={handleClose}>{t("dialog.cancel")}</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				fullWidth={true}
				open={showDisinviteDialog}
				onClose={handleClose}
			>
				<DialogTitle>{t("gateway.disinviteDialogTitle")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						<Trans
							i18nKey="gateway.disinviteDialogMsg"
							values={{ name: selectedUser?.username }}
						/>
						<Trans
							i18nKey="gateway.msgFrom"
							values={{ from: gatewayLabel }}
						/>
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button color="danger" loading={isDisinviting} onClick={disinviteUser}>{t("gateway.disinvite")}</Button>
					<Button color="inherit" onClick={handleClose}>{t("dialog.cancel")}</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default GatewayUsers;
